import { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Chip, IconButton, Link, Tooltip } from "@mui/material";
import { Icon } from "@iconify/react";
import copyFill from "@iconify/icons-eva/copy-fill";
import { fDateTime } from "src/utils/formatTime";

const dateFormat = "yyyy-MM-dd hh:mm a";
const CREDIT_COLOR = "#2e7d32";
const DEBIT_COLOR = "#d32f2f";

const emptyCell = <span style={{ color: "#999" }}>—</span>;

// Each entry is one wallet balance change. A top-up carries the payment that
// funded it, a charging debit the session it paid for; an entry with neither is
// a manual edit.
export const walletEntryType = (row) => {
  if (row.payment_id) {
    return { label: "Top-up", color: "success", hint: "Wallet top-up via payment" };
  }
  if (row.transaction_id) {
    return { label: "Charging", color: "info", hint: "Charging session debit" };
  }
  return {
    label: "Manual",
    color: "warning",
    hint: "Manual adjustment, not linked to a payment or charging session",
  };
};

const toNumber = (value) => Number.parseFloat(value ?? 0);
const signColor = (value) => {
  if (value > 0) return CREDIT_COLOR;
  if (value < 0) return DEBIT_COLOR;
  return "inherit";
};

const CopyButton = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef();
  useEffect(() => () => clearTimeout(resetTimer.current), []);
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        clearTimeout(resetTimer.current);
        resetTimer.current = setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  };
  return (
    <Tooltip title={copied ? "Copied" : `Copy ${label}`}>
      <IconButton
        size="small"
        onClick={handleCopy}
        aria-label={`Copy ${label}`}
        sx={{ p: 0.5, ml: 0.5, flexShrink: 0 }}
      >
        <Icon icon={copyFill} width={14} height={14} />
      </IconButton>
    </Tooltip>
  );
};

// Rows grow to fit (getRowHeight 'auto' in WalletHistory), so long values wrap
// instead of being cut off.
const wrapText = { fontSize: "0.875rem", wordBreak: "break-all", py: 1 };

export const WalletHistoryListColumns = () => [
  {
    field: "modify_time",
    headerName: "Date",
    type: "dateTime",
    minWidth: 150,
    flex: 1,
    valueGetter: ({ value }) => (value ? new Date(value) : null),
    valueFormatter: ({ value }) => fDateTime(value, dateFormat),
  },
  {
    field: "type",
    headerName: "Type",
    minWidth: 100,
    flex: 0.6,
    valueGetter: ({ row }) => walletEntryType(row).label,
    renderCell: ({ row }) => {
      const { label, color, hint } = walletEntryType(row);
      return (
        <Tooltip title={hint}>
          <Chip label={label} color={color} size="small" variant="outlined" />
        </Tooltip>
      );
    },
  },
  {
    field: "amount_changed",
    headerName: "Amount",
    type: "number",
    minWidth: 100,
    flex: 0.7,
    valueGetter: ({ value }) => toNumber(value),
    renderCell: ({ value }) => {
      let sign = "";
      if (value > 0) sign = "+";
      else if (value < 0) sign = "−";
      return (
        <span style={{ fontWeight: 600, color: signColor(value) }}>
          {sign}
          {Math.abs(value).toFixed(2)}
        </span>
      );
    },
  },
  {
    field: "current_balance",
    headerName: "Balance",
    type: "number",
    minWidth: 100,
    flex: 0.7,
    valueGetter: ({ value }) => toNumber(value),
    renderCell: ({ value }) => (
      <span style={{ color: value < 0 ? DEBIT_COLOR : undefined }}>
        {value.toFixed(2)}
      </span>
    ),
  },
  {
    field: "transaction_id",
    headerName: "Transaction ID",
    minWidth: 120,
    flex: 0.8,
    renderCell: ({ value }) => {
      if (!value) return emptyCell;
      return (
        <Link
          component={RouterLink}
          to={`/transactions/${value}`}
          underline="hover"
          sx={{ fontWeight: 500, fontSize: "0.875rem" }}
        >
          {value}
        </Link>
      );
    },
  },
  {
    field: "payment_id",
    headerName: "Payment ID",
    minWidth: 180,
    flex: 1.3,
    sortable: false,
    renderCell: ({ value }) => {
      if (!value) return emptyCell;
      return (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          {/* The Payment Details page (refund-details route) loads any
              payment_details row by id, wallet top-ups included. */}
          <Link
            component={RouterLink}
            to={`/refund-details/${value}`}
            underline="hover"
            sx={{ ...wrapText, fontWeight: 500 }}
          >
            {value}
          </Link>
          <CopyButton value={value} label="Payment ID" />
        </Box>
      );
    },
  },
  {
    field: "note",
    headerName: "Remarks",
    minWidth: 120,
    flex: 1,
    renderCell: ({ value }) =>
      value ? (
        <Box
          component="span"
          sx={{ ...wrapText, display: "block", wordBreak: "break-word" }}
        >
          {value}
        </Box>
      ) : (
        emptyCell
      ),
  },
  {
    field: "id_tag",
    headerName: "Tag Id",
    minWidth: 110,
    flex: 0.8,
  },
];
